import { ISpeechService } from 'src/services/SpeechService/ISpeechService'

import { Scenes } from '../constants/game'
import { IAudioPlayerService } from '../services/AudioPlayerService/IAudioPlayerService'
import { IExecutor } from './IExecutor'
import { COPY, COPY_VARIATION, DYNAMIC_COPY } from 'src/constants/copy'
import { VictimService } from 'src/services/VictimService/VictimService'
import { AUDIO_TROLLEY_CRASHES } from 'src/constants/audio'
import { AudioQueueManager } from 'src/services/AudioQueueManagerService/AudioQueueManagerService'
import { getRandomCopyVariation } from 'src/utils/getRandomCopyVariation'
import { useCaptionStore } from 'src/state/captionState'

export class WhoLivesQuestionSceneExecutor extends IExecutor {
  private audioPlayer: IAudioPlayerService
  private setScene: (scene: Scenes) => void
  private speech: ISpeechService
  private audioQueueManager: AudioQueueManager

  private victimService: VictimService
  private roundsPlayed = 0

  constructor(
    audioPlayer: IAudioPlayerService,
    setScene: (scene: Scenes) => void,
    speech: ISpeechService,
    audioQueueManager: AudioQueueManager,
    victimService: VictimService,
  ) {
    super()
    this.audioPlayer = audioPlayer
    this.setScene = setScene
    this.speech = speech
    this.audioQueueManager = audioQueueManager
    this.victimService = victimService
  }

  public exec = async () => {
    console.log('🐄 WhoLivesQuestionSceneExecutor exec')
  }

  public sayVictimIntro = async () => {
    this.victimService.recordCurrentPairAppearance()

    // From round two onward, kick things off with a runaway-trolley quip.
    if (this.roundsPlayed > 0) {
      const rollingAgainCopy = getRandomCopyVariation(
        COPY_VARIATION.ROLLING_AGAIN,
      )
      this.audioQueueManager.enqueue(() => this.speech.speak(rollingAgainCopy))
    }
    this.roundsPlayed++

    // Caption-only players (Fire TV has no speech synthesis; muted web)
    // already have both names on the victim plates, so two more captions
    // of "if you veer left you kill..." would just be reading delay.
    if (this.speech.isSilent()) return

    this.audioQueueManager.enqueue(() =>
      this.speech.speak(
        DYNAMIC_COPY.LEFT_VICTIM_INTRO(this.victimService.victimOne.name),
      ),
    )

    this.audioQueueManager.enqueue(() =>
      this.speech.speak(
        DYNAMIC_COPY.RIGHT_VICTIM_INTRO(this.victimService.victimTwo.name),
      ),
    )
  }

  public promptWhoLives = async () => {
    this.audioQueueManager.enqueue(() =>
      this.speech.speak(COPY.WHO_LIVES_QUESTION),
    )
  }

  public getVictims = () => {
    return {
      leftVictim: this.victimService.victimOne,
      rightVictim: this.victimService.victimTwo,
    }
  }

  private handleVictimChoice = (victimName: string) => {
    this.victimService.doomVictim(victimName)

    // The kill lands NOW: drop every intro/prompt line still waiting and
    // cut off the one mid-sentence, so the crash is the very next thing
    // the player hears. (Only speech is cut — SFX are never stopped.)
    this.audioQueueManager.clearPending()
    this.speech.cancelActive()
    // The stale "who will you choose?" must not sit under the SPLAT.
    useCaptionStore.getState().setCaption('')

    const randomCrash = getRandomCopyVariation(AUDIO_TROLLEY_CRASHES)
    this.audioQueueManager.enqueue(() => this.audioPlayer.playSound(randomCrash))

    this.audioQueueManager.enqueue(async () => {
      this.setScene(Scenes.SentenceVictim)
    })
  }

  public handleLeftClick = () => {
    this.handleVictimChoice(this.victimService.victimOne.name)
  }

  public handleRightClick = () => {
    this.handleVictimChoice(this.victimService.victimTwo.name)
  }
}
